import { Environment } from '../env/Environment';
import { testId } from '../utils';
import * as Toast from '@radix-ui/react-toast';
import { useTranslation } from '@rpi/i18n';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import React from 'react';

const reportError = (error: unknown) => {
  if (Environment.ENV_NAME !== 'local') {
    // @ts-expect-error newrelic is on the window
    window.newrelic.noticeError(error);
  }
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      reportError(error);
      console.error('Query error to be sent to monitoring', error, query);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      reportError(error);
      console.error(
        'Mutation error to be sent to monitoring',
        error,
        variables,
        context,
        mutation,
      );
    },
  }),
});

export const ApiProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { t } = useTranslation();

  // Right now, this is a very simple implementation for generic toast notifications
  // that assumes we will never need multiple toasts at once. It also assumes that any
  // subsequent mutation should clear present errors.
  // This can be extended pretty easily, but will require a proper context instead of
  // just this thin provider. At that time we may also want or need to enable toast
  // errors to be set manually from error handlers instead of just from the global
  // backup handler.
  const [mutationError, setMutationError] = React.useState<unknown | undefined>(
    undefined,
  );

  React.useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        refetchOnWindowFocus: false,
        useErrorBoundary: true,
      },
      mutations: {
        onMutate: () => {
          setMutationError(undefined);
        },
        onError: (error) => {
          setMutationError(error);
        },
      },
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Toast.ToastProvider>
        {children}
        <Toast.Root
          {...testId('generic-error-toast')}
          className="grid grid-cols-[auto_max-content] items-center gap-x-[15px] rounded-md bg-red-light text-red-dark p-[15px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] [grid-template-areas:_'title_action'_'description_action'] data-[swipe=cancel]:translate-x-0 data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[state=closed]:animate-hide data-[state=open]:animate-slideIn data-[swipe=end]:animate-swipeOut data-[swipe=cancel]:transition-[transform_200ms_ease-out]"
          open={!!mutationError}
        >
          <Toast.Title>{t('route.auth.generic_error')}</Toast.Title>
        </Toast.Root>
        <Toast.Viewport className="fixed bottom-0 right-0 z-[2147483647] m-0 flex w-[390px] max-w-[100vw] list-none flex-col gap-2.5 p-[var(--viewport-padding)] outline-none [--viewport-padding:_25px]" />
      </Toast.ToastProvider>
    </QueryClientProvider>
  );
};
